import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { navigate, useToast, useTRPC } from '@barev/web';
import { useRef, useState } from 'react';
import type { Me } from '../App.tsx';
import { errText, useT } from '../i18n.tsx';
import { Avatar, Dialog, Field, I, Skeleton } from '../components/kit.tsx';
import { statusLabel } from './Orders.tsx';

const ROLES = ['CLEANER', 'HANDYMAN', 'DRIVER', 'TEACHER', 'CAREER_CONSULTANT', 'DOCS_AGENT'] as const;
const LANGS = ['ru', 'en', 'hy', 'zh', 'es'] as const;
type Role = (typeof ROLES)[number];
type Lang = (typeof LANGS)[number];
type Slot = { weekday: number; startMinute: number; endMinute: number };
const hhmm = (m: number) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
const minutes = (s: string) => {
  const [h, m] = s.split(':').map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
};

export function ProviderCard({ id, me }: { id: string; me: Me }) {
  const { t, shared, date, dateTime, amd } = useT();
  const trpc = useTRPC();
  const qc = useQueryClient();
  const toast = useToast();
  const p = useQuery(trpc.bo.providers.get.queryOptions({ id }));
  const dict = useQuery(trpc.bo.providers.dictionaries.queryOptions());
  const [dlg, setDlg] = useState<'edit' | 'block' | 'payout' | null>(null);
  const refresh = () => {
    qc.invalidateQueries({ queryKey: trpc.bo.providers.get.queryKey({ id }) });
    qc.invalidateQueries({ queryKey: trpc.bo.providers.list.queryKey() });
  };
  const onError = (e: unknown) => toast(errText(t, e), 'error');
  const setActive = useMutation(trpc.bo.providers.setActive.mutationOptions({ onSuccess: () => (refresh(), toast(t('prv.saved'))), onError }));
  const reset = useMutation(trpc.bo.providers.resetInvite.mutationOptions({ onSuccess: refresh, onError }));
  const removeDoc = useMutation(trpc.bo.providers.removeDoc.mutationOptions({ onSuccess: refresh, onError }));
  const chat = useMutation(trpc.bo.chats.openFor.mutationOptions({ onSuccess: (r) => navigate(`/chats/${r.id}`), onError }));
  const canChat = me.roles.some((r) => r === 'SUPERADMIN' || r === 'SUPPORT');

  const back = (
    <a className="bo-back" href="#/providers">
      <I name="back" size={16} />
      {t('prv.back')}
    </a>
  );
  if (p.isError)
    return (
      <>
        {back}
        <div className="bo-panel bo-empty">{errText(t, p.error)}</div>
      </>
    );
  if (!p.data)
    return (
      <div className="bo-col">
        <Skeleton h={90} />
        <Skeleton h={300} />
      </div>
    );
  const d = p.data;
  const districtName = new Map((dict.data?.districts ?? []).map((x) => [x.id, shared(`districts.${x.code}`)]));
  const q = d.quality;
  const offers30 = q.accepted30 + q.declined30 + q.expired30;

  return (
    <>
      {back}
      <header className="bo-profile">
        <Avatar name={d.name} id={d.id} size={64} />
        <div style={{ flex: 1, minWidth: 220 }}>
          <h1 className="bo-title" style={{ fontSize: 'clamp(26px, 3.4vw, 38px)' }}>
            {d.name}
          </h1>
          <div className="bo-muted" style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: '4px 12px', alignItems: 'center' }}>
            <span>{shared(`roles.${d.role}`)}</span>
            {d.company && <span>{d.company.name}</span>}
            {d.phone && <a href={`tel:${d.phone.replace(/\s/g, '')}`}>{d.phone}</a>}
            {d.username && <span>@{d.username}</span>}
            {d.active ? (
              d.onShift ? <span className="bo-chip bo-chip--ok">{t('prv.onShift')}</span> : <span className="bo-chip">{t('prv.offShift')}</span>
            ) : (
              <span className="bo-chip bo-chip--danger">{t('prv.blocked')}</span>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {canChat && d.linked && (
            <button type="button" className="bo-btn bo-btn--ghost" onClick={() => chat.mutate({ providerId: d.id })}>
              <I name="chats" size={16} />
              {t('prv.write')}
            </button>
          )}
          <button type="button" className="bo-btn" onClick={() => setDlg('edit')}>
            {t('prv.edit')}
          </button>
        </div>
      </header>

      {!d.active && (
        <div className="bo-banner" style={{ background: 'var(--bc-pink)', color: 'var(--bc-plum)' }}>
          <span>
            <b>{t('prv.blockedSince', { date: d.blockedAt ? date(d.blockedAt) : '—' })}</b>
            {d.blockedReason && ` ${d.blockedReason}`}
          </span>
          <button type="button" className="bo-btn bo-btn--sm" onClick={() => setActive.mutate({ id: d.id, active: true })} disabled={setActive.isPending}>
            {t('prv.unblock')}
          </button>
        </div>
      )}
      {!d.linked && d.inviteUrl && (
        <div className="bo-banner">
          <span>{t('prv.inviteHint')}</span>
          <span style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              type="button"
              className="bo-btn bo-btn--sm"
              onClick={() => {
                void navigator.clipboard?.writeText(d.inviteUrl!);
                toast(t('ui.copied'));
              }}
            >
              {t('prv.copyInvite')}
            </button>
          </span>
        </div>
      )}

      <div className="bo-client-grid">
        <div className="bo-col">
          <section className="bo-panel">
            <div className="bo-panel__head">
              <h2 className="bo-h2">{t('prv.quality')}</h2>
              <span className="bo-faint bo-small">{t('prv.days30')}</span>
            </div>
            <div className="bo-quality">
              <div>
                <b className="bo-bignum">{q.rating != null ? `★ ${q.rating}` : '—'}</b>
                <span className="bo-small bo-muted">{t('prv.ratings', { n: q.ratingCount })}</span>
              </div>
              <div>
                <b className="bo-bignum">{offers30 ? `${Math.round((q.accepted30 / offers30) * 100)}%` : '—'}</b>
                <span className="bo-small bo-muted">{t('prv.acceptRate', { a: q.accepted30, n: offers30 })}</span>
              </div>
              <div>
                <b className="bo-bignum" style={{ color: q.declined30 + q.expired30 >= 3 ? 'var(--bc-danger)' : undefined }}>
                  {q.declined30 + q.expired30}
                </b>
                <span className="bo-small bo-muted">{t('prv.refusals', { d: q.declined30, e: q.expired30 })}</span>
              </div>
              <div>
                <b className="bo-bignum" style={{ color: q.low30 ? 'var(--bc-danger)' : undefined }}>
                  {q.low30}
                </b>
                <span className="bo-small bo-muted">{t('prv.lowRatings')}</span>
              </div>
            </div>
            {d.ratings.length > 0 && (
              <ul className="bo-list" style={{ marginTop: 14 }}>
                {d.ratings.slice(0, 8).map((r) => (
                  <li key={r.id} style={{ alignItems: 'flex-start' }}>
                    <span className="bo-stars" style={{ minWidth: 76 }} data-low={r.score <= 3 || undefined}>
                      {'★'.repeat(r.score)}
                      {'☆'.repeat(5 - r.score)}
                    </span>
                    <span className="bo-grow" style={{ whiteSpace: 'normal' }}>
                      {r.comment ?? <span className="bo-faint">{t('ord.noComment')}</span>}
                      <span className="bo-small bo-faint" style={{ display: 'block' }}>
                        <a href={`#/orders/${r.orderId}`}>№{r.orderNumber}</a> · {r.client} · {date(r.at)}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="bo-panel">
            <div className="bo-panel__head">
              <h2 className="bo-h2">{t('prv.upcoming')}</h2>
            </div>
            {d.upcoming.length === 0 ? (
              <p className="bo-hint">{t('prv.noUpcoming')}</p>
            ) : (
              <ul className="bo-list">
                {d.upcoming.map((o) => (
                  <li key={o.id}>
                    <a href={`#/orders/${o.id}`} className="bo-grow" style={{ color: 'inherit', textDecoration: 'none' }}>
                      <b>
                        №{o.number} · {shared(`services.${o.service}.name`)}
                      </b>
                      <span className="bo-small bo-muted" style={{ display: 'block' }}>
                        {dateTime(o.scheduledAt)}
                        {o.district && ` · ${shared(`districts.${o.district}`)}`}
                      </span>
                    </a>
                    <span className="bo-chip">{statusLabel(shared, o.flow, o.status)}</span>
                  </li>
                ))}
              </ul>
            )}
            {d.recent.length > 0 && (
              <p className="bo-hint" style={{ marginTop: 12 }}>
                {t('prv.recent')}:{' '}
                {d.recent.slice(0, 8).map((o, i) => (
                  <span key={o.id}>
                    {i > 0 && ', '}
                    <a href={`#/orders/${o.id}`}>№{o.number}</a>
                    {o.rating && ` ★${o.rating}`}
                  </span>
                ))}
              </p>
            )}
          </section>
        </div>

        <div className="bo-col">
          {d.money && (
            <section className="bo-panel bo-money-card">
              <div className="bo-panel__head" style={{ marginBottom: 6 }}>
                <span className="bo-label" style={{ color: 'inherit', opacity: 0.75 }}>
                  {t('prv.balance')}
                </span>
                <span className="bo-small">{rateText(t, d.money.rate)}</span>
              </div>
              <div className="bo-bignum">{amd(d.money.balance)}</div>
              <div className="bo-small" style={{ display: 'flex', gap: 16, flexWrap: 'wrap', margin: '8px 0 12px' }}>
                <span>
                  {t('prv.thisMonth')} <b className="bo-num">{amd(d.money.thisMonth)}</b>
                </span>
                <span>
                  {t('prv.prevMonth')} <b className="bo-num">{amd(d.money.prevMonth)}</b>
                </span>
                <span>
                  {t('prv.paidTotal')} <b className="bo-num">{amd(d.money.paidTotal)}</b>
                </span>
              </div>
              <button type="button" className="bo-btn bo-btn--sm" disabled={d.money.balance <= 0} onClick={() => setDlg('payout')}>
                {t('prv.payout')}
              </button>
              {d.money.payouts.length > 0 && (
                <ul className="bo-list" style={{ marginTop: 12 }}>
                  {d.money.payouts.slice(0, 6).map((x) => (
                    <li key={x.id}>
                      <span className="bo-grow">
                        <b className="bo-num">{amd(x.amount)}</b>
                        <span className="bo-small" style={{ display: 'block', opacity: 0.75 }}>
                          {[x.paidAt && date(x.paidAt), x.by, x.note].filter(Boolean).join(' · ')}
                        </span>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )}

          <section className="bo-panel">
            <div className="bo-panel__head">
              <h2 className="bo-h2">{t('prv.work')}</h2>
            </div>
            <dl className="bo-kv">
              <dt>{t('prv.languages')}</dt>
              <dd>{d.languages.join(', ').toUpperCase()}</dd>
              <dt>{t('prv.districts')}</dt>
              <dd>{d.districtIds.length ? d.districtIds.map((x) => districtName.get(x) ?? '').join(', ') : t('prv.anyDistrict')}</dd>
            </dl>
            <WeekView schedule={d.schedule} />
            {d.adminNote && <p className="bo-side-panel__note" style={{ marginTop: 12 }}>{d.adminNote}</p>}
          </section>

          <DocsPanel providerId={d.id} docs={d.docs} onChanged={refresh} onRemove={(docId) => confirm(t('prv.removeDocConfirm')) && removeDoc.mutate({ id: docId })} />

          <section className="bo-panel">
            <div className="bo-panel__head">
              <h2 className="bo-h2">{t('prv.access')}</h2>
            </div>
            <div className="bo-col" style={{ gap: 8 }}>
              {d.active ? (
                <button type="button" className="bo-btn bo-btn--danger bo-btn--sm" onClick={() => setDlg('block')}>
                  {t('prv.block')}
                </button>
              ) : (
                <button type="button" className="bo-btn bo-btn--sm" onClick={() => setActive.mutate({ id: d.id, active: true })}>
                  {t('prv.unblock')}
                </button>
              )}
              <button type="button" className="bo-btn bo-btn--ghost bo-btn--sm" onClick={() => confirm(t('prv.resetConfirm')) && reset.mutate({ id: d.id })} disabled={reset.isPending}>
                {t('prv.resetInvite')}
              </button>
              <p className="bo-hint">{t('prv.since', { date: date(d.createdAt) })}</p>
            </div>
          </section>
        </div>
      </div>

      {dlg === 'edit' && <ProviderForm me={me} provider={d} onClose={() => setDlg(null)} />}
      {dlg === 'block' && <BlockDialog id={d.id} onClose={() => setDlg(null)} onDone={refresh} />}
      {dlg === 'payout' && d.money && <PayoutDialog id={d.id} balance={d.money.balance} onClose={() => setDlg(null)} onDone={refresh} />}
    </>
  );
}

function rateText(t: ReturnType<typeof useT>['t'], r: { kind: 'company' | 'preset' | 'own'; type?: string; value?: number | null; name?: string }) {
  if (r.kind === 'preset') return t('prv.ratePreset', { name: r.name ?? '' });
  // сотрудник партнёра «как Uber» — зарплату ему платит партнёр
  if (r.kind === 'company' && r.value == null) return t('prv.ratePartnerPays');
  const v = r.type === 'PERCENT' ? `${r.value}%` : `${r.value} ֏`;
  return r.kind === 'company' ? t('prv.rateCompany', { v }) : t('prv.rateOwn', { v });
}

function WeekView({ schedule }: { schedule: Slot[] }) {
  const { shared } = useT();
  return (
    <div className="bo-week">
      {[0, 1, 2, 3, 4, 5, 6].map((wd) => {
        const s = schedule.filter((x) => x.weekday === wd);
        return (
          <div key={wd} className="bo-week__day" data-off={!s.length || undefined}>
            <b>{shared(`admin.weekdays.${wd}`)}</b>
            <span>{s.length ? s.map((x) => `${hhmm(x.startMinute)}–${hhmm(x.endMinute)}`).join(', ') : '—'}</span>
          </div>
        );
      })}
    </div>
  );
}

function DocsPanel({ providerId, docs, onChanged, onRemove }: { providerId: string; docs: { id: string; title: string; fileName: string; mime: string; size: number; createdAt: Date }[]; onChanged: () => void; onRemove: (id: string) => void }) {
  const { t, date } = useT();
  const toast = useToast();
  const file = useRef<HTMLInputElement>(null);
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);
  const upload = async (f: File) => {
    if (f.size > 10 * 1024 * 1024) return toast(t('chat.fileTooBig'), 'error');
    setBusy(true);
    try {
      const form = new FormData();
      form.set('title', title || f.name);
      form.set('file', f);
      const res = await fetch(`/files/bo/provider-doc/${providerId}`, { method: 'POST', body: form, credentials: 'include' });
      if (!res.ok) throw new Error(((await res.json().catch(() => ({}))) as { error?: string }).error ?? String(res.status));
      setTitle('');
      onChanged();
      toast(t('prv.docAdded'));
    } catch (e) {
      toast(errText(t, e), 'error');
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="bo-panel">
      <div className="bo-panel__head">
        <h2 className="bo-h2">{t('prv.docs')}</h2>
        <span className="bo-faint bo-small">{docs.length}</span>
      </div>
      {docs.length === 0 ? (
        <p className="bo-hint">{t('prv.noDocsHint')}</p>
      ) : (
        <ul className="bo-list">
          {docs.map((d) => (
            <li key={d.id}>
              <I name="file" size={20} />
              <a className="bo-grow" href={`/files/bo/provider-doc-file/${d.id}`} target="_blank" rel="noreferrer" style={{ color: 'inherit', minWidth: 0 }}>
                <b>{d.title}</b>
                <span className="bo-small bo-faint" style={{ display: 'block' }}>
                  {d.fileName} · {date(d.createdAt)}
                </span>
              </a>
              <button type="button" className="bo-iconbtn" onClick={() => onRemove(d.id)} aria-label={t('chat.tplDelete')}>
                <I name="close" size={16} />
              </button>
            </li>
          ))}
        </ul>
      )}
      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <input className="bo-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t('prv.docTitlePh')} maxLength={120} style={{ minHeight: 36, padding: '6px 12px' }} />
        <button type="button" className="bo-btn bo-btn--sm" onClick={() => file.current?.click()} disabled={busy}>
          <I name="clip" size={16} />
          {t('prv.upload')}
        </button>
        <input
          ref={file}
          type="file"
          hidden
          accept="image/*,application/pdf"
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = '';
            if (f) void upload(f);
          }}
        />
      </div>
    </section>
  );
}

function BlockDialog({ id, onClose, onDone }: { id: string; onClose: () => void; onDone: () => void }) {
  const { t } = useT();
  const trpc = useTRPC();
  const toast = useToast();
  const [reason, setReason] = useState('');
  const save = useMutation(
    trpc.bo.providers.setActive.mutationOptions({
      onSuccess: () => {
        onDone();
        onClose();
      },
      onError: (e) => toast(errText(t, e), 'error'),
    }),
  );
  return (
    <Dialog
      open
      onClose={onClose}
      title={t('prv.blockTitle')}
      footer={
        <>
          <button type="button" className="bo-btn bo-btn--ghost" onClick={onClose}>
            {t('ui.close')}
          </button>
          <button type="button" className="bo-btn bo-btn--accent" disabled={!reason.trim() || save.isPending} onClick={() => save.mutate({ id, active: false, reason })}>
            {t('prv.block')}
          </button>
        </>
      }
    >
      <Field label={t('prv.blockReason')}>
        <textarea className="bo-textarea" value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} placeholder={t('prv.blockReasonPh')} />
      </Field>
      <p className="bo-hint">{t('prv.blockHint')}</p>
    </Dialog>
  );
}

function PayoutDialog({ id, balance, onClose, onDone }: { id: string; balance: number; onClose: () => void; onDone: () => void }) {
  const { t, amd } = useT();
  const trpc = useTRPC();
  const toast = useToast();
  const [amount, setAmount] = useState(String(balance));
  const [note, setNote] = useState('');
  const save = useMutation(
    trpc.bo.providers.payout.mutationOptions({
      onSuccess: () => {
        onDone();
        toast(t('prv.payoutDone'));
        onClose();
      },
      onError: (e) => toast(errText(t, e), 'error'),
    }),
  );
  return (
    <Dialog
      open
      onClose={onClose}
      title={t('prv.payoutTitle')}
      footer={
        <>
          <button type="button" className="bo-btn bo-btn--ghost" onClick={onClose}>
            {t('ui.close')}
          </button>
          <button type="button" className="bo-btn" disabled={!Number(amount) || save.isPending} onClick={() => save.mutate({ id, amount: Number(amount), note: note || undefined })}>
            {t('prv.payoutConfirm')}
          </button>
        </>
      }
    >
      <Field label={t('prv.amount')} hint={t('prv.balanceHint', { sum: amd(balance) })}>
        <input className="bo-input" inputMode="numeric" value={amount} onChange={(e) => setAmount(e.target.value.replace(/\D/g, ''))} />
      </Field>
      <Field label={t('prv.payoutNote')}>
        <input className="bo-input" value={note} maxLength={300} onChange={(e) => setNote(e.target.value)} placeholder={t('prv.payoutNotePh')} />
      </Field>
    </Dialog>
  );
}

// ───── форма исполнителя ─────

type ProviderData = {
  id: string;
  name: string;
  phone: string | null;
  role: string;
  company: { id: string } | null;
  languages: string[];
  districtIds: string[];
  schedule: Slot[];
  adminNote: string | null;
  money: { rateType: string; rateValue: number; payoutPresetId: string | null; presets: { id: string; name: string }[] } | null;
};

export function ProviderForm({ me, provider, onClose }: { me: Me; provider?: ProviderData; onClose: () => void }) {
  const { t, shared } = useT();
  const trpc = useTRPC();
  const qc = useQueryClient();
  const toast = useToast();
  const dict = useQuery(trpc.bo.providers.dictionaries.queryOptions());
  const isSuper = me.roles.includes('SUPERADMIN');
  const [f, setF] = useState({
    name: provider?.name ?? '',
    phone: provider?.phone ?? '',
    role: (provider?.role ?? 'CLEANER') as Role,
    companyId: provider?.company?.id ?? '',
    languages: (provider?.languages ?? ['ru']) as Lang[],
    districtIds: provider?.districtIds ?? [],
    adminNote: provider?.adminNote ?? '',
    rateType: provider?.money?.rateType ?? 'PERCENT',
    rateValue: String(provider?.money?.rateValue ?? 70),
    payoutPresetId: provider?.money?.payoutPresetId ?? '',
  });
  // график: одно окно на день (так заведено в рабочем боте); дни без окна — выходные
  const [week, setWeek] = useState<(null | { from: string; to: string })[]>(() =>
    [0, 1, 2, 3, 4, 5, 6].map((wd) => {
      const s = provider ? provider.schedule.find((x) => x.weekday === wd) : wd < 6 ? { startMinute: 540, endMinute: 1200 } : null;
      return s ? { from: hhmm(s.startMinute), to: hhmm(s.endMinute) } : null;
    }),
  );
  const done = (r?: { id: string; inviteUrl: string } | void) => {
    qc.invalidateQueries({ queryKey: trpc.bo.providers.list.queryKey() });
    qc.invalidateQueries({ queryKey: trpc.bo.providers.get.queryKey() });
    toast(t('prv.saved'));
    onClose();
    if (r && 'id' in r) navigate(`/providers/${r.id}`);
  };
  const onError = (e: unknown) => toast(errText(t, e), 'error');
  const create = useMutation(trpc.bo.providers.create.mutationOptions({ onSuccess: done, onError }));
  const update = useMutation(trpc.bo.providers.update.mutationOptions({ onSuccess: () => done(), onError }));
  const companies = (dict.data?.companies ?? []).filter((c) => c.roles.includes(f.role));
  const payload = {
    name: f.name,
    phone: f.phone || null,
    role: f.role,
    companyId: f.companyId || null,
    languages: f.languages,
    districtIds: f.districtIds,
    adminNote: f.adminNote || null,
    schedule: week.flatMap((w, wd) => (w ? [{ weekday: wd, startMinute: minutes(w.from), endMinute: minutes(w.to) }] : [])),
    rate: isSuper ? { rateType: f.rateType as 'FIXED' | 'PERCENT', rateValue: Number(f.rateValue) || 0, payoutPresetId: f.payoutPresetId || null } : undefined,
  };
  const toggle = <T,>(list: T[], v: T) => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

  return (
    <Dialog
      open
      wide
      onClose={onClose}
      title={provider ? t('prv.editTitle') : t('prv.addTitle')}
      footer={
        <>
          <button type="button" className="bo-btn bo-btn--ghost" onClick={onClose}>
            {t('ui.close')}
          </button>
          <button
            type="button"
            className="bo-btn"
            disabled={!f.name.trim() || !f.languages.length || create.isPending || update.isPending}
            onClick={() => (provider ? update.mutate({ id: provider.id, ...payload }) : create.mutate(payload))}
          >
            {t('ui.save')}
          </button>
        </>
      }
    >
      <div className="bo-row2">
        <Field label={t('prv.name')}>
          <input className="bo-input" value={f.name} maxLength={100} onChange={(e) => setF({ ...f, name: e.target.value })} />
        </Field>
        <Field label={t('chat.phone')}>
          <input className="bo-input" value={f.phone} maxLength={30} onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder="+374 …" />
        </Field>
      </div>
      <div className="bo-row2">
        <Field label={t('prv.role')}>
          <select className="bo-select" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value as Role, companyId: '' })}>
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {shared(`roles.${r}`)}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t('prv.company')}>
          <select className="bo-select" value={f.companyId} onChange={(e) => setF({ ...f, companyId: e.target.value })}>
            <option value="">{t('prv.freelance')}</option>
            {companies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <div>
        <span className="bo-label">{t('prv.languages')}</span>
        <div className="bo-pills" style={{ marginTop: 6 }}>
          {LANGS.map((l) => (
            <button key={l} type="button" className="bo-pill" aria-pressed={f.languages.includes(l)} onClick={() => setF({ ...f, languages: toggle(f.languages, l) })}>
              {l.toUpperCase()}
            </button>
          ))}
        </div>
      </div>
      <div>
        <span className="bo-label">{t('prv.districts')}</span>
        <div className="bo-pills" style={{ marginTop: 6 }}>
          {(dict.data?.districts ?? []).map((d) => (
            <button key={d.id} type="button" className="bo-pill" aria-pressed={f.districtIds.includes(d.id)} onClick={() => setF({ ...f, districtIds: toggle(f.districtIds, d.id) })}>
              {shared(`districts.${d.code}`)}
            </button>
          ))}
        </div>
        <span className="bo-hint">{t('prv.districtsHint')}</span>
      </div>
      <div>
        <span className="bo-label">{t('prv.schedule')}</span>
        <div className="bo-sched">
          {week.map((w, wd) => (
            <div key={wd} className="bo-sched__row" data-off={!w || undefined}>
              <label className="bo-check" style={{ minWidth: 64 }}>
                <input type="checkbox" checked={!!w} onChange={(e) => setWeek(week.map((x, i) => (i === wd ? (e.target.checked ? { from: '09:00', to: '20:00' } : null) : x)))} />
                {shared(`admin.weekdays.${wd}`)}
              </label>
              {w && (
                <>
                  <input className="bo-input" type="time" value={w.from} step={900} onChange={(e) => setWeek(week.map((x, i) => (i === wd ? { ...w, from: e.target.value } : x)))} />
                  <span>–</span>
                  <input className="bo-input" type="time" value={w.to} step={900} onChange={(e) => setWeek(week.map((x, i) => (i === wd ? { ...w, to: e.target.value } : x)))} />
                </>
              )}
            </div>
          ))}
        </div>
      </div>
      {isSuper && !f.companyId && (
        <div className="bo-row2">
          <Field label={t('prv.rate')}>
            <select className="bo-select" value={f.payoutPresetId || f.rateType} onChange={(e) => (e.target.value === 'PERCENT' || e.target.value === 'FIXED' ? setF({ ...f, payoutPresetId: '', rateType: e.target.value }) : setF({ ...f, payoutPresetId: e.target.value }))}>
              <option value="PERCENT">{shared('admin.rate.PERCENT')}</option>
              <option value="FIXED">{shared('admin.rate.FIXED')}</option>
              {(provider?.money?.presets ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {t('prv.ratePreset', { name: p.name })}
                </option>
              ))}
            </select>
          </Field>
          {!f.payoutPresetId && (
            <Field label={f.rateType === 'PERCENT' ? '%' : '֏'}>
              <input className="bo-input" inputMode="numeric" value={f.rateValue} onChange={(e) => setF({ ...f, rateValue: e.target.value.replace(/\D/g, '') })} />
            </Field>
          )}
        </div>
      )}
      <Field label={t('prv.note')}>
        <textarea className="bo-textarea" style={{ minHeight: 70 }} value={f.adminNote} maxLength={4000} onChange={(e) => setF({ ...f, adminNote: e.target.value })} placeholder={t('card.notePh')} />
      </Field>
    </Dialog>
  );
}
